/**
 * PricingPage — Preispläne & Pakete
 *
 * Zeigt alle Pricing-Pläne und Add-ons.
 * Plan-Wahl startet den echten Stripe-Checkout (usePlanSelection); ohne
 * Stripe-Konfiguration fällt sie auf den ehrlichen Vormerk-Pfad zurück.
 *
 * Features:
 *   - Monatlich / Jährlich Toggle
 *   - Trial-Einstiegskarte (Stufe 1) + 4 Plan-Karten (Wachstum hervorgehoben als empfohlener Kernplan)
 *   - Enterprise als Anfrage-Karte (requestOnly → Kontakt-Flow statt Checkout)
 *   - Zielgruppen-Filter mit personalisierter Empfehlung
 *   - Add-on-Bereich mit Multi-Brand-Erklärung
 *   - FAQ Accordion
 *   - Bottom CTA
 *   - Deep-Link-Highlight via ?highlight=<planId>:
 *       hebt die Zielkarte mit grünem Ring hervor, scrollt einmalig dorthin,
 *       leitet automatisch den passenden Audience-Filter ab (initialAutoAudience),
 *       zeigt einen Kontext-Banner mit Dismiss-Button,
 *       koordiniert Ring + Banner + Audience beim Schließen.
 *       Genutzt von UpgradeNudge-Links aus EvergreenPage, ContentToolsPage usw.
 *
 * Datenquelle: src/data/pricing.js (keine Hardcodings in dieser Datei)
 */

import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { trackEvent } from "../lib/trackEvent";
import { getVariant } from "../lib/ab_utils";
import { Check, Plus, ChevronDown, ChevronUp, Tag, Zap, Star, X, Info } from "lucide-react";
import {
  trialConfig,
  yearlyDiscountInfo,
  pricingPlans,
  pricingServices,
  pricingAddons,
  pricingFormatModules,
  pricingFaq,
  audienceRecommendations,
  recommendPlanForAudience,
  getPlanById,
} from "../data/pricing";
import { usePlanSelection } from "../hooks/usePlanSelection";
import { useKanalVerfuegbarkeit, KANAL_FALLBACK } from "../hooks/useKanalVerfuegbarkeit";

// Kanal-Wahrheit kommt aus dem gemeinsamen Haken — siehe hooks/useKanalVerfuegbarkeit.
// Vorher stand hier eine eigene Kopie; zwei Abrufe derselben Wahrheit sind einer zu viel.

// ── Hilfsfunktion: Preis je Toggle ───────────────────────────────────────────

function getDisplayPrice(plan, yearly) {
  return yearly ? plan.priceYearly : plan.priceMonthly;
}

function getYearlySavings(plan) {
  return (plan.priceMonthly - plan.priceYearly) * 12;
}

// ── HighlightContextBanner ────────────────────────────────────────────────────
//
// Zeigt einen kurzen Kontext-Hinweis, wenn die Seite per Deep-Link mit einem
// ?highlight=<planId> aufgerufen wurde. Hat einen Dismiss-Button (×).
// Wird koordiniert mit Ring-Highlight und auto-gesetztem Audience-State.

const HighlightContextBanner = ({ plan, audienceLabel, onDismiss }) => (
  <div className="flex items-start justify-between gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl max-w-3xl mx-auto">
    <div className="flex items-start gap-2">
      <Info size={15} className="flex-shrink-0 mt-0.5 text-emerald-600" />
      <p className="text-sm text-emerald-700 leading-snug">
        Wir haben den <strong>{plan.name}</strong>-Plan für dich hervorgehoben
        {audienceLabel ? (
          <> — passend für <strong>{audienceLabel}</strong></>
        ) : (
          ""
        )}.
      </p>
    </div>
    <button
      onClick={onDismiss}
      aria-label="Hervorhebung schließen"
      className="flex-shrink-0 text-emerald-500 hover:text-emerald-700 transition-colors p-0.5 rounded"
    >
      <X size={15} />
    </button>
  </div>
);

// ── PlanCard ──────────────────────────────────────────────────────────────────

const PlanCard = ({ plan, yearly, isRecommendedForAudience, isSelected, onSelect }) => {
  const price = getDisplayPrice(plan, yearly);
  const savings = getYearlySavings(plan);

  return (
    <div
      className={`premium-card relative flex flex-col overflow-hidden ${
        plan.highlighted ? "premium-card-glow" : ""
      }`}
    >
      {/* Plan badge — rechts oben */}
      {plan.badge && (
        <div
          className={`absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-bl-xl ${
            plan.highlighted
              ? "bg-fox-fire text-white"
              : "bg-stone-100 text-stone-600"
          }`}
        >
          {plan.badge}
        </div>
      )}

      {/* Audience-Empfehlung — links oben */}
      {isRecommendedForAudience && (
        <div className="absolute top-0 left-0 px-3 py-1 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-br-xl flex items-center gap-1">
          <Star size={10} />
          Empfohlen für dich
        </div>
      )}

      {/* Plan-Header */}
      <div
        className={`px-6 pt-6 pb-5 ${
          plan.highlighted ? "bg-gradient-to-b from-orange-50 to-white" : "bg-white"
        }`}
      >
        <h3 className="font-heading font-bold text-xl text-stone-800">{plan.name}</h3>
        <p className="text-xs text-stone-500 mt-1 leading-snug">{plan.description}</p>

        {/* Klartext-Versprechen — was der Kunde konkret bekommt, in seiner Sprache */}
        {plan.promise && (
          <p className={`mt-3 text-sm font-semibold leading-snug rounded-lg px-3 py-2 ${
            plan.highlighted ? "bg-orange-100/70 text-orange-900" : "bg-stone-100 text-stone-700"
          }`}>
            {plan.promise}
          </p>
        )}

        {/* Preis-Anzeige */}
        <div className="mt-4">
          {plan.priceLabel ? (
            <div className="flex items-end gap-1">
              <span className="text-3xl font-bold font-heading text-stone-900">{plan.priceLabel}</span>
              <span className="text-sm text-stone-400 mb-1">· Preis auf Anfrage</span>
            </div>
          ) : (
            <>
              <div className="flex items-end gap-1">
                <span className="text-3xl font-bold font-heading text-stone-900">{price} €</span>
                <span className="text-sm text-stone-400 mb-1">/Monat</span>
              </div>
              {yearly ? (
                <p className="text-xs text-emerald-600 font-medium mt-0.5">
                  Du sparst {savings} €/Jahr
                </p>
              ) : (
                <p className="text-xs text-stone-400 mt-0.5">
                  {plan.priceYearly} €/Monat bei Jahreszahlung
                </p>
              )}
            </>
          )}
        </div>

        {/* CTA-Button */}
        <button
          onClick={() => onSelect?.(plan.id)}
          className={`w-full mt-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${
            isSelected
              ? "bg-emerald-600 text-white shadow-md shadow-emerald-100"
              : plan.highlighted
              ? "bg-fox-fire text-white hover:bg-fox-fire-dark shadow-md shadow-orange-200"
              : "border-2 border-stone-200 text-stone-700 hover:border-stone-400 hover:bg-stone-50"
          }`}
        >
          {isSelected ? (
            <>
              <Check size={15} />
              Ausgewählt
            </>
          ) : (
            plan.cta
          )}
        </button>

        {/* Trial-Hinweis — nur für buchbare Pläne (nicht für Anfrage-Stufen wie Enterprise) */}
        {trialConfig.enabled && !plan.requestOnly && !isSelected && (
          <p className="text-xs text-stone-400 text-center mt-2">
            {trialConfig.days} Tage kostenlos · danach wählst du aktiv — keine automatische Abbuchung
          </p>
        )}
      </div>

      {/* Feature-Liste */}
      <div className="px-6 py-5 bg-white flex-1 border-t border-stone-100">
        {/* Ideal-für Chips */}
        {plan.idealFor?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {plan.idealFor.map((tag) => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        <ul className="space-y-2">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2 text-sm text-stone-600">
              <Check
                size={14}
                className={`flex-shrink-0 mt-0.5 ${
                  plan.highlighted ? "text-fox-fire" : "text-emerald-500"
                }`}
              />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ── TrialCard ─────────────────────────────────────────────────────────────────
//
// Stufe 1 der Paketlogik: die kostenlose Testphase als eigene Einstiegskarte
// VOR den Bezahlplänen. Bewusst breit (volle Grid-Breite) und optisch leichter
// als die Bezahlkarten — sie senkt die Hürde, konkurriert aber nicht mit dem
// hervorgehobenen Wachstum-Plan. Datenquelle: trialConfig.card (pricing.js).

const TrialCard = ({ onStart }) => {
  const card = trialConfig.card;
  if (!trialConfig.enabled || !card) return null;
  return (
    <div className="premium-card relative overflow-hidden max-w-5xl mx-auto">
      <div className="absolute top-0 right-0 px-3 py-1 text-xs font-bold rounded-bl-xl bg-emerald-500 text-white">
        {card.badge}
      </div>
      <div className="px-6 py-6 bg-gradient-to-r from-emerald-50/70 to-white flex flex-col md:flex-row md:items-center gap-5">
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-xl text-stone-800">{card.name}</h3>
          <p className="text-sm font-semibold text-emerald-700 mt-1">{card.promise}</p>
          <p className="text-xs text-stone-500 mt-1.5 leading-snug max-w-xl">{card.description}</p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {card.features.map((feature) => (
              <li key={feature} className="flex items-start gap-2 text-sm text-stone-600">
                <Check size={14} className="flex-shrink-0 mt-0.5 text-emerald-500" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col items-stretch md:items-end gap-2 flex-shrink-0">
          <div className="flex items-end gap-1 md:justify-end">
            <span className="text-3xl font-bold font-heading text-stone-900">0 €</span>
            <span className="text-sm text-stone-400 mb-1">/ {trialConfig.days} Tage</span>
          </div>
          <button
            onClick={onStart}
            className="px-6 py-2.5 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors shadow-md shadow-emerald-100"
          >
            {card.cta}
          </button>
          <p className="text-xs text-stone-400 md:text-right">{trialConfig.disclaimer}</p>
        </div>
      </div>
    </div>
  );
};

// ── AddonCard ─────────────────────────────────────────────────────────────────

const AddonCard = ({ addon, highlighted }) => {
  const isComingSoon = addon.status === "coming_soon";
  return (
    <div
      className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
        isComingSoon
          ? "border-stone-200 bg-stone-50 opacity-75"
          : highlighted
          ? "border-emerald-300 bg-emerald-50"
          : "bg-white border-stone-200 hover:border-stone-300"
      }`}
    >
      <span className="text-2xl flex-shrink-0">{addon.icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-stone-800">{addon.name}</span>
            {isComingSoon && (
              <span className="text-xs px-1.5 py-0.5 bg-stone-100 text-stone-500 border border-stone-200 rounded-full">
                Demnächst
              </span>
            )}
          </div>
          <span className="text-sm font-bold text-stone-700 flex-shrink-0">
            {/* DOC-02: `priceLabel` erlaubt „auf Anfrage" für Add-ons, die es noch
                nicht gibt. Vorher stand hier zwangsläufig ein Eurobetrag — auch für
                etwas, das weder buchbar noch gebaut war. */}
            {addon.priceLabel ?? addon.priceMonthlyRange ?? `+ ${addon.priceMonthly} €/Monat`}
          </span>
        </div>
        <p className="text-xs text-stone-500 mt-0.5 leading-snug">{addon.description}</p>
        {addon.idealFor?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {addon.idealFor.map((tag) => (
              <span
                key={tag}
                className="text-xs px-1.5 py-0.5 bg-stone-50 border border-stone-200 text-stone-400 rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── FormatModuleCard ──────────────────────────────────────────────────────────
//
// Zeigt ein Format-Modul mit Status-Guard:
//   status: "coming_soon" → gedimmte Karte, „Demnächst"-Badge, disabled CTA
//   status: "beta"        → aktive Karte, „Beta"-Badge, CTA „Beta-Zugang anfragen"
//   status: "available"   → normale Karte ohne Badge, aktiver CTA

const STATUS_CONFIG = {
  coming_soon: {
    badgeLabel: "Demnächst verfügbar",
    badgeClass: "bg-stone-100 text-stone-500 border border-stone-200",
    cardClass:  "border-stone-200 bg-stone-50 opacity-75",
    ctaLabel:   "Interesse vormerken",
    ctaClass:   "border border-stone-200 text-stone-400 cursor-not-allowed",
    ctaDisabled: true,
  },
  beta: {
    badgeLabel: "Beta",
    badgeClass: "bg-purple-100 text-purple-600 border border-purple-200",
    cardClass:  "border-purple-200 bg-white hover:border-purple-300",
    ctaLabel:   "Beta-Zugang anfragen",
    ctaClass:   "border border-purple-300 text-purple-700 hover:bg-purple-50",
    ctaDisabled: false,
  },
  available: {
    badgeLabel: null,
    badgeClass: "",
    cardClass:  "border-stone-200 bg-white hover:border-stone-300",
    ctaLabel:   "Hinzubuchen",
    ctaClass:   "border border-stone-200 text-stone-700 hover:border-stone-400 hover:bg-stone-50",
    ctaDisabled: false,
  },
};

const FormatModuleCard = ({ module }) => {
  const cfg = STATUS_CONFIG[module.status] ?? STATUS_CONFIG.coming_soon;

  return (
    <div className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${cfg.cardClass}`}>
      <span className="text-2xl flex-shrink-0">{module.icon}</span>
      <div className="flex-1 min-w-0 space-y-1.5">

        {/* Name + Badge */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-stone-800">{module.name}</span>
          {cfg.badgeLabel && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.badgeClass}`}>
              {cfg.badgeLabel}
            </span>
          )}
        </div>

        {/* Beschreibung */}
        <p className="text-xs text-stone-500 leading-snug">{module.description}</p>

        {/* Hint */}
        {module.hint && (
          <p className="text-xs text-stone-400 italic leading-snug">{module.hint}</p>
        )}

        {/* Preisrange + CTA */}
        <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
          <span className="text-xs font-medium text-stone-500">
            {module.status === "coming_soon"
              ? `Geplant: ${module.priceMonthlyRange}`
              : module.priceMonthlyRange}
          </span>
          <button
            disabled={cfg.ctaDisabled}
            className={`text-xs px-3 py-1.5 rounded-lg transition-colors flex-shrink-0 ${cfg.ctaClass}`}
          >
            {cfg.ctaLabel}
          </button>
        </div>

      </div>
    </div>
  );
};

// ── FaqItem ───────────────────────────────────────────────────────────────────

const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-stone-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50 transition-colors"
      >
        <span className="text-sm font-medium text-stone-700">{item.q}</span>
        {open ? (
          <ChevronUp size={16} className="text-stone-400 flex-shrink-0" />
        ) : (
          <ChevronDown size={16} className="text-stone-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-4 pb-3">
          <p className="text-sm text-stone-500 leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────

const PricingPage = () => {
  const navigate = useNavigate();
  const [yearly, setYearly] = useState(false);
  const { selectedPlanId, selectPlan } = usePlanSelection();
  const { satz: kanalSatz } = useKanalVerfuegbarkeit();

  // ── Deep-Link-Highlight: /pricing?highlight=<planId>&audience=<key> ──────
  // highlight: hebt die Zielkarte hervor, scrollt dorthin.
  // audience:  wählt den Audience-Filter direkt vor (Vorrang vor plan-basierter Ableitung).
  //            Wird vom Planfinder-Wizard gesetzt, damit die exakt gewählte Zielgruppe
  //            aktiv ist — auch wenn zwei Audiences denselben primaryPlan haben.
  const [searchParams] = useSearchParams();
  const rawHighlight = searchParams.get("highlight");
  const highlightPlanId = getPlanById(rawHighlight) ? rawHighlight : null;

  // Audience-Key Priorität:
  //   1. ?audience=<key>  (explizit vom Planfinder übergeben)
  //   2. Ableitung aus highlightPlanId (bestehende Deep-Link-Logik)
  //   3. null
  const rawAudience = searchParams.get("audience");
  const initialAutoAudience =
    rawAudience && audienceRecommendations[rawAudience]
      ? rawAudience
      : highlightPlanId
      ? (Object.entries(audienceRecommendations).find(
          ([, rec]) => rec.primaryPlan === highlightPlanId
        )?.[0] ?? null)
      : null;

  // Audience-State: startet vom auto-abgeleiteten Wert (oder null), dann manuell steuerbar
  const [selectedAudience, setSelectedAudience] = useState(initialAutoAudience);

  // Dismiss-State: ein einzelner Toggle koordiniert Ring + Banner + Auto-Audience-Reset
  const [highlightDismissed, setHighlightDismissed] = useState(false);

  // Ref auf den auto-gesetzten Audience-Key — für koordinierten Reset beim Dismiss.
  // Bleibt stabil über Renders (initialer Wert genügt).
  const autoAudienceRef = useRef(initialAutoAudience);

  // Ref: hat der Nutzer nach dem Laden manuell eine Audience ausgewählt?
  // Verhindert, dass Dismiss eine bewusste manuelle Auswahl zurücksetzt.
  const audienceManuallyChanged = useRef(false);

  // Ref auf das Wrapper-Div der Zielkarte (für einmaligen Scroll)
  const highlightRef = useRef(null);
  // Guard: verhindert Re-Scroll bei State-Änderungen (Toggle, Audience-Filter)
  const hasScrolled = useRef(false);
  const ab03Variant = useRef("A");

  useEffect(() => {
    ab03Variant.current = getVariant("AB-03", { trackFn: trackEvent });
    trackEvent("pricing_page_viewed", {
      referrer: document.referrer || "",
      highlight_plan: highlightPlanId || null,
      ab03_variant: ab03Variant.current,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!highlightPlanId || hasScrolled.current) return;
    const el = highlightRef.current;
    if (!el) return;
    // 150 ms Verzögerung: sichert vollständiges erstes Render ab
    const timer = setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      hasScrolled.current = true;
    }, 150);
    return () => clearTimeout(timer);
  }, [highlightPlanId]); // läuft nur wenn sich die Plan-ID ändert (= beim Laden)

  // Manuelle Audience-Auswahl — markiert, dass der Nutzer aktiv interagiert hat
  const handleAudienceSelect = (key) => {
    audienceManuallyChanged.current = true;
    setSelectedAudience(selectedAudience === key ? null : key);
  };

  // Dismiss: entfernt Ring + Banner + resets Auto-Audience (nur wenn nicht manuell überschrieben)
  const handleDismissHighlight = () => {
    setHighlightDismissed(true);
    if (!audienceManuallyChanged.current && autoAudienceRef.current !== null) {
      setSelectedAudience(null);
    }
  };

  const recommendation = selectedAudience
    ? recommendPlanForAudience(selectedAudience)
    : null;

  // Aktive Highlight-ID: null sobald dismissed (kein Ring, kein Banner, kein Scroll-Ziel)
  const activeHighlightId = highlightDismissed ? null : highlightPlanId;

  return (
    <div className="space-y-12 pb-8">

      {/* ── Seitenheader (cinematic) ──────────────────────────────────────── */}
      <div className="cinematic rounded-3xl px-6 py-12 text-center">
        <div className="orb orb-fire" style={{ width: 280, height: 280, top: -60, left: "18%" }} />
        <div className="orb orb-indigo" style={{ width: 300, height: 300, bottom: -90, right: "12%" }} />
        <div className="relative z-10 space-y-3 reveal">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 border border-white/15 backdrop-blur rounded-full text-xs text-white/85 font-medium">
            <Tag size={12} className="text-brand-gold" />
            Einfache Preise · monatlich kündbar · keine Überraschungen
          </div>
          <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-white leading-tight max-w-3xl mx-auto">
            {ab03Variant.current === "B"
              ? <>Dein erster <span className="text-cinematic">Monatsplan</span> in 2 Minuten</>
              : <>Social Media ohne Aufwand — <span className="text-cinematic">für alle</span>, die keine Zeit haben</>}
          </h1>
          <p className="text-white/60 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
            Kein Tech-Wissen nötig. Starte heute kostenlos — monatlich kündbar.
          </p>
        </div>
      </div>

      {/* ── Monatlich / Jährlich Toggle ───────────────────────────────────── */}
      <div className="flex items-center justify-center gap-4">
        <span
          className={`text-sm font-medium transition-colors ${
            !yearly ? "text-stone-800" : "text-stone-400"
          }`}
        >
          Monatlich
        </span>
        <button
          onClick={() => setYearly(!yearly)}
          aria-pressed={yearly}
          aria-label="Auf Jahreszahlung wechseln"
          className={`relative w-12 h-6 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fox-fire ${
            yearly ? "bg-fox-fire" : "bg-stone-200"
          }`}
        >
          <span
            className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${
              yearly ? "translate-x-6" : "translate-x-0"
            }`}
          />
        </button>
        <span
          className={`text-sm font-medium transition-colors ${
            yearly ? "text-stone-800" : "text-stone-400"
          }`}
        >
          Jährlich
          <span className="ml-2 text-xs text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded-full">
            {yearlyDiscountInfo.label}
          </span>
        </span>
      </div>

      {/* Kontextabhängiger Kündigungs-Hinweis — löst „jederzeit kündbar vs. 12 Monate Jahresabo" auf */}
      <p className="text-center text-xs text-stone-400 -mt-8">
        {yearly
          ? "Jahresabo: 12 Monate Laufzeit — dafür dauerhaft günstiger. Lieber flexibel? Monatlich zahlen, jederzeit kündbar."
          : "Monatlich kündbar, keine Mindestlaufzeit. Für den Test ist keine Kreditkarte nötig."}
        <br />
        Alle Preise sind Endpreise — gemäß §&nbsp;19 UStG wird keine Umsatzsteuer berechnet.
      </p>

      {/* ── Zielgruppen-Filter ────────────────────────────────────────────── */}
      <div className="space-y-3">
        <p className="text-center text-xs text-stone-400">
          Für wen suchst du einen Plan?
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {Object.entries(audienceRecommendations).map(([key, rec]) => (
            <button
              key={key}
              onClick={() => handleAudienceSelect(key)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                selectedAudience === key
                  ? "bg-fox-fire text-white border-fox-fire shadow-sm"
                  : "bg-white text-stone-600 border-stone-200 hover:border-fox-fire hover:text-fox-fire"
              }`}
            >
              {rec.label}
            </button>
          ))}
        </div>

        {/* Audience-Empfehlungs-Hinweis */}
        {recommendation && (
          <div className="flex items-start gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl max-w-xl mx-auto">
            <Zap size={16} className="flex-shrink-0 mt-0.5 text-emerald-600" />
            <p className="text-sm text-emerald-700 leading-snug">{recommendation.note}</p>
          </div>
        )}
      </div>

      {/* ── Kontext-Banner (Deep-Link) ────────────────────────────────────── */}
      {/* Nur sichtbar, wenn per ?highlight=<planId> verlinkt und nicht dismissed. */}
      {activeHighlightId && (
        <HighlightContextBanner
          plan={getPlanById(activeHighlightId)}
          audienceLabel={
            initialAutoAudience
              ? audienceRecommendations[initialAutoAudience]?.label
              : null
          }
          onDismiss={handleDismissHighlight}
        />
      )}

      {/* ── Stufe 1: Testphase (Einstiegskarte) ───────────────────────────── */}
      <TrialCard
        onStart={() => {
          trackEvent("pricing_trial_cta_clicked", { ab03_variant: ab03Variant.current });
          navigate("/login?tab=register");
        }}
      />

      {/* ── Stufen 2–5: Plan-Karten (Starter · Wachstum · Dominanz · Enterprise) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 max-w-7xl mx-auto">
        {pricingPlans.filter((p) => !p.hidden).map((plan) => {
          const isDeepLinked = plan.id === activeHighlightId;
          return (
            // Wrapper-Div trägt den Scroll-Ref und den optionalen Highlight-Ring.
            // Der Ring (box-shadow) liegt außerhalb von PlanCards border-2 — beide
            // Hervorhebungen (z.B. Wachstum-Orange + Deep-Link-Grün) koexistieren.
            <div
              key={plan.id}
              ref={isDeepLinked ? highlightRef : null}
              className={`rounded-2xl transition-shadow ${
                isDeepLinked
                  ? "ring-2 ring-emerald-400 shadow-lg shadow-emerald-50"
                  : ""
              }`}
            >
              <PlanCard
                plan={plan}
                yearly={yearly}
                isRecommendedForAudience={recommendation?.primaryPlan === plan.id}
                isSelected={plan.id === selectedPlanId}
                onSelect={(planId) =>
                  // Anfrage-Stufen (Enterprise) verzweigt selectPlan in den
                  // Kontakt-Flow — kein Checkout, kein mailto-Sonderfall mehr.
                  selectPlan(planId, { billingPeriod: yearly ? "yearly" : "monthly" })}
              />
            </div>
          );
        })}
      </div>

      {/* ── Kanäle & Verteilung + Was du bekommst (Ehrlichkeits-/Trust-Boxen) ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
        <div className="rounded-xl border border-stone-200 bg-white px-5 py-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-1.5">Kanäle & Verteilung</h3>
          <p className="text-sm text-stone-500 leading-relaxed">
            {/* Kommt live aus /api/public/kanal-verfuegbarkeit — dieselbe Quelle, die
                auch der Verbinden-Riegel liest. Ein fester Text wäre am Tag der
                Meta-Freigabe wieder falsch, dann zu klein. */}
            <strong className="text-stone-700">{kanalSatz || KANAL_FALLBACK}</strong>{" "}
            Die übrigen Kanäle sind noch in der Freigabe durch den jeweiligen Anbieter —
            bis dahin bekommst du jeden Beitrag fertig formatiert zum Kopieren &amp;
            Einplanen (1 Klick: Text + Hashtags + Bildidee). Dein Monatsplan wird nur
            für Kanäle erzeugt, die wirklich senden können; für einen gesperrten Kanal
            produzieren wir keine Beiträge ins Leere.
          </p>
          <p className="text-sm text-stone-500 leading-relaxed mt-2">
            Jedes Paket hat eine klare Tages-Schlagzahl: Starter bespielt 1 Kanal täglich,
            Wachstum 3 Kanäle täglich, Dominanz alle verfügbaren Kanäle mit je 3 Beiträgen
            am Tag. Jede Kanal-Veröffentlichung zählt als 1 Beitrag. Du brauchst mehr —
            mehrere Marken, Teams oder Sonderworkflows? Dafür gibt es Enterprise als
            individuelle Anfrage, eine kurze Nachricht genügt.
          </p>
        </div>
        <div className="rounded-xl border border-stone-200 bg-white px-5 py-4">
          <h3 className="text-sm font-semibold text-stone-800 mb-1.5">Was du bekommst</h3>
          <p className="text-sm text-stone-500 leading-relaxed">
            Abwechslungsreiche Inhalte statt Einheitsbrei: Bild-Beiträge, kurze Video-Snippets
            und längere Videos, Karussell-Entwürfe und saisonale Aktionen. Die Mischung plant
            Förderfuchs automatisch passend zu deiner Marke und deinen Kanälen — feste
            Format-Quoten gibt es bewusst nicht.
          </p>
        </div>
      </div>

      {/* ── Feste Service-Angebote (Kontakt statt Checkout) ───────────────── */}
      <div className="space-y-5">
        <div className="text-center">
          <h2 className="font-heading font-bold text-2xl text-stone-900">Weitere Förderfuchs-Services</h2>
          <p className="text-sm text-stone-500 mt-1">Feste Angebote — persönlich auf Anfrage, ohne Checkout.</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
          {pricingServices.map((s) => (
            <Link
              key={s.id}
              to={s.to}
              data-testid={`service-${s.id}`}
              className="premium-card group flex flex-col p-6"
            >
              <div className="flex items-center gap-3">
                <span className="text-3xl flex-shrink-0">{s.icon}</span>
                <div className="min-w-0">
                  <h3 className="font-heading font-bold text-lg text-stone-800">{s.name}</h3>
                  <p className="text-sm font-semibold text-fox-fire">{s.priceLabel}</p>
                </div>
              </div>
              <p className="text-sm text-stone-600 mt-3 flex-1">{s.description}</p>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {s.idealFor.map((t) => (
                  <span key={t} className="text-xs px-2 py-0.5 bg-stone-100 text-stone-500 rounded-full">{t}</span>
                ))}
              </div>
              <span className="mt-5 inline-flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold border-2 border-fox-fire text-fox-fire group-hover:bg-fox-fire group-hover:text-white transition-colors">
                {s.cta} →
              </span>
            </Link>
          ))}
        </div>
      </div>

      {/* ── Add-ons ───────────────────────────────────────────────────────── */}
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Plus size={18} className="text-stone-500" />
          </div>
          <div>
            <h2 className="font-heading font-bold text-xl text-stone-800">Add-ons</h2>
            <p className="text-sm text-stone-500">
              Ergänze deinen Plan flexibel mit weiteren Modulen — auf Anfrage zubuchbar, wir schalten sie für dich frei.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {pricingAddons.map((addon) => (
            <AddonCard
              key={addon.id}
              addon={addon}
              highlighted={recommendation?.suggestedAddons?.includes(addon.id) ?? false}
            />
          ))}
        </div>

        {/* Multi-Brand / Multi-Account Erklärung */}
        <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 space-y-2">
          <h3 className="font-medium text-sm text-stone-700 flex items-center gap-2">
            <span>🏢</span>
            Mehrere Marken oder Standorte?
          </h3>
          <p className="text-sm text-stone-500 leading-relaxed">
            Mit dem <strong>Extra Standort / Extra Brand Add-on</strong> (29 €/Monat) kannst
            du dein bestehendes Paket schrittweise erweitern — ohne direkt auf einen
            teureren Plan wechseln zu müssen. Das ist ideal für Franchise-Betriebe,
            Mehrmarken-Unternehmen oder Agenturen, die erst wenige Kunden betreuen.
          </p>
          <p className="text-xs text-stone-400 leading-relaxed">
            Ab mehreren Marken oder Kunden lohnt sich das{" "}
            <strong>Enterprise-Paket</strong> mit getrennten Kunden-Workspaces und
            Team-Zugang — individuell zugeschnitten, auf Anfrage.
          </p>
        </div>
      </div>

      {/* ── Format-Module (Coming Soon) ───────────────────────────────────── */}
      {pricingFormatModules.length > 0 && (
        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-stone-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <span className="text-base">🚀</span>
            </div>
            <div>
              <h2 className="font-heading font-bold text-xl text-stone-800">
                Höherwertige Formate
              </h2>
              <p className="text-sm text-stone-500">
                Karussells, Videos und Reels — KI-generiert direkt aus deinem Content.
                Diese Module kommen bald.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pricingFormatModules.map((module) => (
              <FormatModuleCard key={module.id} module={module} />
            ))}
          </div>

          {/* Erklärungshinweis */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3">
            <p className="text-xs text-stone-500 leading-relaxed">
              <span className="font-medium text-stone-600">Warum höherwertige Formate?</span>{" "}
              Karussells erzielen auf Instagram und LinkedIn bis zu 3× mehr Reichweite als
              einzelne Bild-Posts. Reels und Shorts sind derzeit die organisch stärksten
              Formate auf TikTok, YouTube und Instagram — diese Module machen sie
              so einfach planbar wie einen normalen Text-Post.
            </p>
          </div>
        </div>
      )}

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <div className="space-y-4">
        <h2 className="font-heading font-bold text-xl text-stone-800">
          Häufige Fragen
        </h2>
        <div className="space-y-2 max-w-2xl">
          {pricingFaq.map((item, i) => (
            <FaqItem key={i} item={item} />
          ))}
        </div>
      </div>

      {/* ── Bottom CTA ────────────────────────────────────────────────────── */}
      <div className="text-center py-6 space-y-3 border-t border-stone-100">
        <p className="text-stone-500 text-sm">Noch unsicher, welcher Plan passt?</p>
        <button
          className="px-6 py-3 rounded-xl bg-fox-fire text-white font-semibold hover:bg-fox-fire-dark transition-colors shadow-md shadow-orange-200"
          onClick={() => {
            trackEvent("pricing_cta_clicked", { cta_position: "bottom", ab03_variant: ab03Variant.current });
            navigate("/planfinder");
          }}
        >
          Passenden Plan finden
        </button>
        <p className="text-xs text-stone-400">
          {trialConfig.days} Tage kostenlos testen · ohne Kreditkarte · keine Einrichtungsgebühr
        </p>
        <p className="text-xs text-stone-400">{trialConfig.channelNote}</p>
      </div>

    </div>
  );
};

export default PricingPage;
